// src/components/Systems.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { GradientMesh } from './ui/Motion';

// Systems data based on Isabella's actual experience
const systemsData = [
  {
    id: 'system1',
    title: 'Legal Lead Generation Engine',
    description: 'Comprehensive lead generation infrastructure for law firms specializing in personal injury and mass torts, optimized for quality and compliance.',
    background: 'linear-gradient(45deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3))',
    gradient: 'primary',
    details: {
      overview: 'End-to-end infrastructure designed to capture, qualify, and convert high-value legal clients across multiple practice areas, with rigorous compliance standards.',
      stages: [
        {
          name: 'Paid Media Acquisition',
          description: 'Multi-channel campaigns across Search, Display, Native, and programmatic networks with specialized legal targeting parameters.'
        },
        {
          name: 'Qualification System',
          description: 'Sophisticated intake forms with branching logic to pre-qualify leads based on case criteria, jurisdiction, and claim value.'
        },
        {
          name: 'CRM Integration',
          description: 'Seamless data transfer to firm case management systems with automated notifications and follow-up sequences.'
        },
        {
          name: 'Attribution & Analytics',
          description: 'Closed-loop reporting that tracks from ad impression to signed case, with ROI calculations based on case values.'
        }
      ]
    }
  },
  {
    id: 'system2',
    title: 'In-House Marketing Infrastructure',
    description: 'Strategic framework for transitioning external agency operations to robust in-house marketing capabilities with advanced tracking and measurement.',
    background: 'linear-gradient(45deg, rgba(6, 182, 212, 0.3), rgba(59, 130, 246, 0.3))',
    gradient: 'secondary',
    details: {
      overview: 'Comprehensive operations infrastructure that enables brands to bring marketing operations in-house while maintaining or improving performance metrics.',
      stages: [
        {
          name: 'Technical Foundation',
          description: 'Implementation of enterprise-grade tracking architecture including server-side integrations, CAPI setup, and cross-domain measurement.'
        },
        {
          name: 'Audience Framework',
          description: 'Development of sophisticated audience targeting frameworks customized to each market and product category.'
        },
        {
          name: 'Team Development',
          description: 'Structured training programs that transfer technical skills and strategic capabilities to internal teams.'
        },
        {
          name: 'Performance Optimization',
          description: 'Ongoing testing frameworks and optimization protocols that continuously improve marketing effectiveness.'
        }
      ]
    }
  },
  {
    id: 'system3',
    title: 'Financial Services Marketing Engine',
    description: 'Compliant, high-performing lead generation system for financial advisors and wealth management professionals with sophisticated qualification flows.',
    background: 'linear-gradient(45deg, rgba(245, 158, 11, 0.3), rgba(239, 68, 68, 0.3))',
    gradient: 'accent',
    details: {
      overview: 'End-to-end marketing infrastructure for financial service professionals that balances compliance requirements with effective lead generation.',
      stages: [
        {
          name: 'Strategic Content Development',
          description: 'Educational content frameworks that position advisors as authoritative resources while meeting financial regulatory requirements.'
        },
        {
          name: 'Multi-Channel Distribution',
          description: 'Integrated campaigns across email, social, search, and programmatic that maximize reach to qualified prospects.'
        },
        {
          name: 'Lead Scoring System',
          description: 'Behavioral and demographic scoring models that prioritize prospects based on assets, timeline, and engagement metrics.'
        },
        {
          name: 'Nurturing Automation',
          description: 'Sophisticated drip sequences that develop relationships over time with compliance-reviewed messaging.'
        }
      ]
    }
  }
];

const Systems = () => {
  const [activeSystem, setActiveSystem] = useState(null);
  
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const handleSystemClick = (id) => {
    setActiveSystem(activeSystem === id ? null : id);
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.215, 0.61, 0.355, 1]
      }
    }
  };

  return (
    <SystemsSection id="process">
      <GradientMesh
        blobs={[
          { size: '32vw', top: '10%', right: '-8%', color: '#8b5cf6', opacity: 0.13, blur: 120, dur: 28 },
          { size: '28vw', bottom: '-6%', left: '-6%', color: '#ec4899', opacity: 0.12, blur: 120, dur: 32, delay: 4 },
        ]}
      />
      <Container>
        <SectionHeader
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <Eyebrow>How I work</Eyebrow>
          <Title className="gradient-text-primary">My Marketing Systems</Title>
          <Divider className="gradient-bg-primary" />
          <Description>
            I specialize in building end-to-end marketing systems that translate strategy into infrastructure that delivers measurable results.
          </Description>
        </SectionHeader>
        
        <SystemsGrid
          ref={ref}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {systemsData.map((system) => (
            <SystemCard
              key={system.id}
              as={motion.div}
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="bg-glass"
              onClick={() => handleSystemClick(system.id)}
              isActive={activeSystem === system.id}
            >
              <SystemHeader style={{ background: system.background }}>
                <SystemTitle className={`gradient-text-${system.gradient}`}>
                  {system.title}
                </SystemTitle>
              </SystemHeader>
              
              <SystemContent>
                <SystemDescription>
                  {system.description}
                </SystemDescription>
                <SystemLink className={`gradient-text-${system.gradient}`}>
                  {activeSystem === system.id ? 'View Less' : 'View System'}
                  <LinkIcon>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {activeSystem === system.id ? (
                        <polyline points="18 15 12 9 6 15"></polyline>
                      ) : (
                        <polyline points="6 9 12 15 18 9"></polyline>
                      )}
                    </svg>
                  </LinkIcon>
                </SystemLink>
              </SystemContent>
              
              <AnimatePresence>
                {activeSystem === system.id && (
                  <SystemDetails
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SystemDetailsDivider />
                    <SystemOverview>
                      {system.details.overview}
                    </SystemOverview>
                    
                    <SystemStages>
                      {system.details.stages.map((stage, index) => (
                        <SystemStage key={index}>
                          <SystemStageNumber className={`gradient-text-${system.gradient}`}>
                            {index + 1}
                          </SystemStageNumber>
                          <SystemStageContent>
                            <SystemStageName>{stage.name}</SystemStageName>
                            <SystemStageDescription>
                              {stage.description}
                            </SystemStageDescription>
                          </SystemStageContent>
                        </SystemStage>
                      ))}
                    </SystemStages>
                  </SystemDetails>
                )}
              </AnimatePresence>
            </SystemCard>
          ))}
        </SystemsGrid>
        
        <CallToActionButton
          href="https://calendar.app.google/JNzzrs67cJGNb3Vq8"
          target="_blank"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          Schedule a Strategy Consultation
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"></path>
          </svg>
        </CallToActionButton>
      </Container>
    </SystemsSection>
  );
};

const SystemsSection = styled.section`
  padding: 6rem 0;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, transparent, ${({ theme }) => theme.colors.darker} 50%, transparent);
    pointer-events: none;
    z-index: -1;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 1.5rem;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a78bfa;
  margin-bottom: 0.85rem;
`;

const SectionHeader = styled(motion.div)`
  margin-bottom: 4rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const Divider = styled.div`
  width: 6rem;
  height: 4px;
  border-radius: 2px;
  margin: 0 auto 1.5rem;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.125rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
  }
`;

const SystemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const SystemCard = styled.div`
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
  cursor: pointer;
  transition: transform ${({ theme }) => theme.transitions.default},
              box-shadow ${({ theme }) => theme.transitions.default};
  
  ${({ isActive, theme }) => isActive && `
    transform: translateY(-8px);
    box-shadow: ${theme.shadows.lg};
  `}
`;

const SystemHeader = styled.div`
  padding: 2rem;
  height: 12rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const SystemTitle = styled.h3`
  position: relative;
  z-index: 1;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
`;

const SystemContent = styled.div`
  padding: 2rem;
`;

const SystemDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
`;

const SystemLink = styled.div`
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  gap: 0.5rem;
  transition: gap ${({ theme }) => theme.transitions.default};
  
  &:hover {
    gap: 0.75rem;
  }
`;

const LinkIcon = styled.span`
  display: flex;
  align-items: center;
`;

const SystemDetails = styled(motion.div)`
  padding: 0 2rem 2rem;
  overflow: hidden;
`;

const SystemDetailsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin-bottom: 1.5rem;
`;

const SystemOverview = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
  font-style: italic;
`;

const SystemStages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SystemStage = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const SystemStageNumber = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
`;

const SystemStageContent = styled.div`
  flex: 1;
`;

const SystemStageName = styled.h4`
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const SystemStageDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9375rem;
`;

const CallToActionButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 1rem 1.75rem;
  border-radius: 9999px;
  margin: 3rem auto 0;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
  transition: all ${({ theme }) => theme.transitions.default};
  display: block;
  width: fit-content;
  
  &:hover {
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.6);
    transform: translateY(-2px);
  }
  
  svg {
    transition: transform ${({ theme }) => theme.transitions.default};
  }
  
  &:hover svg {
    transform: translateX(4px);
  }
`;

export default Systems;